import type {
  AuditLogFilters,
  BackupJobFilters,
  CedolinoFilters,
  PreventivoFilters,
  DocumentoFilters,
  ClienteFilters,
  ContrattoFilters,
  LeadFilters,
  MacchinaFilters,
  MachineAlertsFilters,
  BackupPendingGroupFilters,
  ReportRangeParams,
  ServiceEventFilters,
  TicketFilters,
  TaskFilters,
  UUID,
  UtenteFilters,
} from '@/types';

/**
 * Centralised React Query keys.
 *
 * Hierarchical and built from `const` tuples so partial invalidation works:
 * `invalidateQueries({ queryKey: queryKeys.tickets.all })` clears every ticket
 * list and detail, while `queryKeys.tickets.detail(id)` targets exactly one.
 * Defining them here (rather than inline in hooks) is what makes it safe to
 * invalidate from a WebSocket handler that has no idea which lists are mounted.
 */
export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
  },

  dashboard: {
    all: ['dashboard'] as const,
    summary: (clienteId?: UUID) => ['dashboard', 'summary', clienteId ?? 'self'] as const,
    modules: (clienteId?: UUID) => ['dashboard', 'modules', clienteId ?? 'self'] as const,
    adminSummary: ['dashboard', 'admin', 'summary'] as const,
  },

  clienti: {
    all: ['clienti'] as const,
    list: (filters: ClienteFilters) => ['clienti', 'list', filters] as const,
    persi: (filters: ClienteFilters) => ['clienti', 'persi', filters] as const,
    detail: (id: UUID) => ['clienti', 'detail', id] as const,
    fonti: (id: UUID) => ['clienti', 'fonti', id] as const,
    fontiVisibilita: (filters: Record<string, unknown>) => ['clienti', 'fonti', 'visibilita', filters] as const,
    fontiCestino: (id: UUID) => ['clienti', 'fonti', id, 'cestino'] as const,
    fontiRevisioni: (id: UUID, fonteId: UUID) => ['clienti', 'fonti', id, fonteId, 'revisioni'] as const,
    fontiAssegnazioni: (id: UUID, fonteId: UUID) => ['clienti', 'fonti', id, fonteId, 'assegnazioni'] as const,
    entita: (id: UUID) => ['clienti', 'entita', id] as const,
    sedi: (id: UUID) => ['clienti', 'sedi', id] as const,
    clientiFinali: (id: UUID) => ['clienti', 'clienti-finali', id] as const,
    struttura: (id: UUID) => ['clienti', 'struttura', id] as const,
    /** @deprecated Alias of `struttura` — same cache key family. */
    organizzazione: (id: UUID) => ['clienti', 'struttura', id] as const,
  },

  prodotti: {
    all: ['prodotti'] as const,
    list: (attivo?: boolean) => ['prodotti', 'list', attivo ?? 'any'] as const,
  },

  serviziCatalog: {
    all: ['servizi-catalog'] as const,
    list: (attivo?: boolean) => ['servizi-catalog', 'list', attivo ?? 'any'] as const,
  },

  contratti: {
    all: ['contratti'] as const,
    list: (filters: ContrattoFilters) => ['contratti', 'list', filters] as const,
    active: (clienteId?: UUID) => ['contratti', 'active', clienteId ?? 'self'] as const,
    detail: (id: UUID) => ['contratti', 'detail', id] as const,
    cestino: (filters: ContrattoFilters) => ['contratti', 'cestino', filters] as const,
    firma: (id: UUID) => ['contratti', 'firma', id] as const,
    macchine: (id: UUID) => ['contratti', 'macchine', id] as const,
  },

  macchine: {
    all: ['macchine'] as const,
    list: (filters: MacchinaFilters) => ['macchine', 'list', filters] as const,
    detail: (id: UUID) => ['macchine', 'detail', id] as const,
    stati: (params: { clienteId?: UUID; modulo?: string; macchinaId?: UUID }) => ['macchine', 'stati', params] as const,
    alerts: (filters: MachineAlertsFilters) => ['macchine', 'alerts', filters] as const,
    alertsCount: ['macchine', 'alerts', 'count'] as const,
  },

  utenti: {
    all: ['utenti'] as const,
    list: (filters: UtenteFilters) => ['utenti', 'list', filters] as const,
    detail: (id: UUID) => ['utenti', 'detail', id] as const,
    operatori: (categoriaId?: UUID) => ['utenti', 'operatori', categoriaId ?? 'all'] as const,
    operatorCategorie: (id: UUID) => ['utenti', 'operatori', id, 'categorie'] as const,
    meCategorie: ['utenti', 'me', 'categorie'] as const,
  },

  tickets: {
    all: ['tickets'] as const,
    list: (filters: TicketFilters) => ['tickets', 'list', filters] as const,
    cestino: (filters: TicketFilters) => ['tickets', 'cestino', filters] as const,
    detail: (id: UUID) => ['tickets', 'detail', id] as const,
    commenti: (id: UUID) => ['tickets', 'commenti', id] as const,
    allegati: (id: UUID) => ['tickets', 'allegati', id] as const,
    categorie: ['tickets', 'categorie'] as const,
    slaPolicies: ['tickets', 'sla-policies'] as const,
    stakeholderCandidates: (params: { search?: string; clienteId?: UUID }) =>
      ['tickets', 'stakeholder-candidates', params] as const,
  },

  tasks: {
    all: ['tasks'] as const,
    list: (filters: TaskFilters) => ['tasks', 'list', filters] as const,
    cestino: (filters: TaskFilters) => ['tasks', 'cestino', filters] as const,
    detail: (id: UUID) => ['tasks', 'detail', id] as const,
  },

  backup: {
    all: ['backup'] as const,
    list: (filters: BackupJobFilters) => ['backup', 'list', filters] as const,
    detail: (id: UUID) => ['backup', 'detail', id] as const,
    latest: (limit: number, clienteId?: UUID) => ['backup', 'latest', limit, clienteId ?? 'self'] as const,
    pending: {
      all: ['backup', 'pending'] as const,
      groups: (filters: BackupPendingGroupFilters) => ['backup', 'pending', 'groups', filters] as const,
      count: ['backup', 'pending', 'count'] as const,
    },
  },

  events: {
    all: ['events'] as const,
    list: (filters: ServiceEventFilters) => ['events', 'list', filters] as const,
    detail: (id: UUID) => ['events', 'detail', id] as const,
    bySeverity: (filters: ServiceEventFilters) => ['events', 'aggregate', 'severity', filters] as const,
    byType: (filters: ServiceEventFilters) => ['events', 'aggregate', 'type', filters] as const,
    types: (modulo?: string) => ['events', 'types', modulo ?? 'all'] as const,
  },

  notifiche: {
    all: ['notifiche'] as const,
    list: (params: { page?: number; pageSize?: number; letta?: boolean; search?: string; categoria?: string }) =>
      ['notifiche', 'list', params] as const,
    unreadCount: ['notifiche', 'unread-count'] as const,
    riepilogo: ['notifiche', 'riepilogo'] as const,
  },

  reports: {
    all: ['reports'] as const,
    backup: (params: ReportRangeParams) => ['reports', 'backup', params] as const,
    tickets: (params: ReportRangeParams) => ['reports', 'tickets', params] as const,
    events: (params: ReportRangeParams & { modulo?: string }) => ['reports', 'events', params] as const,
    clienti: (params: ReportRangeParams) => ['reports', 'admin', 'clienti', params] as const,
    uptime: (params: ReportRangeParams) => ['reports', 'admin', 'uptime', params] as const,
  },

  apiKeys: {
    all: ['api-keys'] as const,
    list: (clienteId?: UUID) => ['api-keys', 'list', clienteId ?? 'all'] as const,
  },

  vaultwarden: {
    all: ['vaultwarden'] as const,
    instances: ['vaultwarden', 'instances'] as const,
    instance: (id: UUID) => ['vaultwarden', 'instance', id] as const,
    users: ['vaultwarden', 'users'] as const,
    memberships: (userId: UUID) => ['vaultwarden', 'memberships', userId] as const,
  },

  audit: {
    all: ['audit'] as const,
    list: (filters: AuditLogFilters) => ['audit', 'list', filters] as const,
  },

  cedolini: {
    all: ['cedolini'] as const,
    list: (filters: CedolinoFilters) => ['cedolini', 'list', filters] as const,
    detail: (id: UUID) => ['cedolini', 'detail', id] as const,
    aggregati: (params: { cliente_id?: UUID; from?: string; to?: string }) => ['cedolini', 'aggregati', params] as const,
    ticketNonFatturati: (params: { cliente_id: UUID; from?: string; to?: string }) =>
      ['cedolini', 'ticket-non-fatturati', params] as const,
  },

  lead: {
    all: ['lead'] as const,
    list: (filters: LeadFilters) => ['lead', 'list', filters] as const,
    detail: (id: UUID) => ['lead', 'detail', id] as const,
    preventivi: (id: UUID) => ['lead', 'preventivi', id] as const,
    messaggi: (id: UUID) => ['lead', 'messaggi', id] as const,
    calendario: (filters: { from: string; to: string; utenteId?: UUID | null; azienda?: boolean; staffAll?: boolean }) =>
      ['lead', 'calendario', filters] as const,
  },

  preventivi: {
    all: ['preventivi'] as const,
    list: (filters: PreventivoFilters) => ['preventivi', 'list', filters] as const,
    detail: (id: UUID) => ['preventivi', 'detail', id] as const,
    eligibili: (clienteId: UUID) => ['preventivi', 'eligibili', clienteId] as const,
    cestino: (filters: PreventivoFilters) => ['preventivi', 'cestino', filters] as const,
    daInviareCount: ['preventivi', 'da-inviare-count'] as const,
    config: ['preventivi', 'config'] as const,
  },

  documenti: {
    all: ['documenti'] as const,
    list: (filters: DocumentoFilters) => ['documenti', 'list', filters] as const,
    detail: (id: UUID) => ['documenti', 'detail', id] as const,
    assegnazioni: (id: UUID) => ['documenti', 'assegnazioni', id] as const,
    accessi: (id: UUID, filters: DocumentoFilters) => ['documenti', 'accessi', id, filters] as const,
    versioni: (id: UUID) => ['documenti', 'versioni', id] as const,
  },

  ai: {
    suggestions: (route: string, entityId?: UUID) => ['ai', 'suggestions', route, entityId ?? 'none'] as const,
  },

  sicurezza: {
    all: ['sicurezza'] as const,
    dashboard: (clienteId: string, periodo: string) => ['sicurezza', 'dashboard', clienteId, periodo] as const,
    eventi: (filtri: unknown) => ['sicurezza', 'eventi', filtri] as const,
    accessi: (clienteId: string, periodo: string) => ['sicurezza', 'accessi', clienteId, periodo] as const,
    firewall: (clienteId: string) => ['sicurezza', 'firewall', clienteId] as const,
    firewallClienti: ['sicurezza', 'firewall-clienti'] as const,
    sorgenti: ['sicurezza', 'sorgenti'] as const,
  },
} as const;
